import { timeEntryTasksQuery } from "@common/api/time-entries"
import type { TimeEntryTaskPublic } from "@common/client"
import {
  FormField,
  FormItem,
  FormMessage,
  useFormField,
} from "@common/components/ui/form"
import { useDebouncedValue } from "@common/hooks/use-debounced-value"
import { taskReference } from "@common/lib/time-entries"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "../fields/label"
import { LookupInput, type LookupOption } from "../fields/lookup-input"

const DEBOUNCE_MS = 200

const OPTION_LIMIT = 20

function optionOf(task: TimeEntryTaskPublic): LookupOption {
  return { id: task.id, label: taskReference(task), hint: task.description }
}

export function TimeEntryTaskField<T extends FieldValues>({
  name,
  label = "Task",
}: {
  name: Path<T>
  label?: string
}): React.ReactElement {
  const { control } = useFormContext<T>()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FieldLabel label={label} />
          <TaskLookup field={field} label={label} />
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function TaskLookup<T extends FieldValues>({
  field,
  label,
}: {
  field: ControllerRenderProps<T, Path<T>>
  label: string
}): React.ReactElement {
  const { formItemId } = useFormField()
  const [typed, setTyped] = useState<string | null>(null)
  const [picked, setPicked] = useState<LookupOption | null>(null)
  const term = useDebouncedValue(typed ?? "", DEBOUNCE_MS).trim()
  const { data } = useQuery(
    timeEntryTasksQuery({ search: term || null, limit: OPTION_LIMIT }),
  )
  const shown = picked && field.value === picked.id ? picked : null

  return (
    <LookupInput
      id={formItemId}
      aria-label={label}
      placeholder="Search tasks…"
      search={typed ?? [shown?.label, shown?.hint].filter(Boolean).join(" ")}
      options={(data?.data ?? []).map(optionOf)}
      onSearch={setTyped}
      onPick={(option) => {
        setTyped(null)
        setPicked(option)
        field.onChange(option.id)
      }}
      onBlur={() => {
        if (typed?.trim() === "") field.onChange("")
        setTyped(null)
        field.onBlur()
      }}
    />
  )
}
